import { getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

import { getLearningProgressRef } from './pathBuilders.js';
import { silentTrack } from './silentTrack.js';

export const LearningProgressService = {
    /**
     * Initialize LearningProgress document
     */
    async initializeDocument(userId, level, chapter, dataParameters = {}) {
        return silentTrack(async () => {
            console.group(`📝 LearningProgressService: initializeDocument`);
            console.log(`🎯 Target: Level ${level} Chapter ${chapter}`);
            console.log(`👤 User: ${userId}`);
            console.log(`📊 Parameters:`, dataParameters);

            const progressRef = getLearningProgressRef(userId, level, chapter);
            const snap = await getDoc(progressRef);

            if (!snap.exists()) {
                console.log(`✨ Creating new LearningProgress document`);
                await setDoc(progressRef, {
                    // CHAPTER METADATA
                    level: parseInt(level),
                    chapter: parseInt(chapter),

                    // CHAPTER PROGRESS
                    chapterUnlocked: parseInt(level) === 2 && parseInt(chapter) === 1,
                    chapterStarted: false,
                    chapterStartDate: null,
                    lastUpdated: serverTimestamp(),
                    lastCompletedModule: null,
                    chapterCompleted: false,
                    chapterCompletionDate: null,

                    // DATA PARAMETERS
                    chapterDataParameters: {
                        theme: 'daily_life',
                        estimatedTime: 45,
                        prerequisites: [],
                        customFields: {},
                        ...dataParameters
                    },

                    // MODULES TRACKING
                    modules: {},

                    // EXPOSURE LISTS (space-saving)
                    vocabularyExposures: [],
                    grammarExposures: [],

                    // SESSIONS
                    sessions: []
                });
                console.log(`✅ Document created successfully`);
            } else {
                console.log(`ℹ️ Document already exists`);
            }
            console.groupEnd();
            return true;
        }, 'initialize_learning_progress');
    },

    /**
     * Mark chapter as started
     */
    async markChapterStarted(userId, level, chapter, dataParameters = {}) {
        return silentTrack(async () => {
            console.log(`🚀 LearningProgress: Mark Chapter Started (L${level} C${chapter})`);
            const progressRef = getLearningProgressRef(userId, level, chapter);
            await updateDoc(progressRef, {
                chapterStarted: true,
                chapterStartDate: serverTimestamp(),
                lastUpdated: serverTimestamp(),
                ...(Object.keys(dataParameters).length > 0 && {
                    chapterDataParameters: {
                        ...dataParameters,
                        startedAt: new Date().toISOString()
                    }
                })
            });
            console.log(`✅ Chapter ${chapter} marked as started`);
        }, 'mark_chapter_started');
    },

    /**
     * Mark chapter as completed
     */
    async markChapterCompleted(userId, level, chapter, dataParameters = {}) {
        return silentTrack(async () => {
            console.log(`🏆 LearningProgress: Mark Chapter Completed (L${level} C${chapter})`);
            const progressRef = getLearningProgressRef(userId, level, chapter);
            await updateDoc(progressRef, {
                chapterCompleted: true,
                chapterCompletionDate: serverTimestamp(),
                lastUpdated: serverTimestamp(),
                ...(Object.keys(dataParameters).length > 0 && {
                    chapterDataParameters: {
                        ...dataParameters,
                        completedAt: new Date().toISOString()
                    }
                })
            });
            console.log(`✅ Chapter ${chapter} marked as completed`);
        }, 'mark_chapter_completed');
    },

    /**
     * Unlock a chapter
     */
    async unlockChapter(userId, level, chapter, dataParameters = {}) {
        return silentTrack(async () => {
            console.log(`🔓 LearningProgress: Unlock Chapter (L${level} C${chapter})`);
            await this.initializeDocument(userId, level, chapter, dataParameters);
            const progressRef = getLearningProgressRef(userId, level, chapter);
            await updateDoc(progressRef, {
                chapterUnlocked: true,
                lastUpdated: serverTimestamp(),
                ...(Object.keys(dataParameters).length > 0 && {
                    chapterDataParameters: {
                        ...dataParameters,
                        unlockedAt: new Date().toISOString()
                    }
                })
            });
            console.log(`✅ Chapter ${chapter} unlocked`);
        }, 'unlock_chapter');
    },

    /**
     * Update module progress
     */
    async updateModuleProgress(userId, level, chapter, moduleKey, progressData, dataParameters = {}) {
        return silentTrack(async () => {
            // Create detailed log group for module update
            console.groupCollapsed(`📊 Module Update: ${moduleKey}`);
            console.log(`Target: L${level} C${chapter}`);
            console.log('Progress Data:', progressData);

            const progressRef = getLearningProgressRef(userId, level, chapter);
            const snap = await getDoc(progressRef);

            if (!snap.exists()) {
                console.log('⚠️ Document missing, initializing...');
                await this.initializeDocument(userId, level, chapter);
            }

            const updates = {
                [`modules.${moduleKey}`]: {
                    ...(snap.data()?.modules?.[moduleKey] || {}),
                    ...progressData,
                    lastOpened: progressData.lastOpened || serverTimestamp()
                },
                lastUpdated: serverTimestamp()
            };

            // If module completed, update lastCompletedModule
            if (progressData.completion) {
                updates.lastCompletedModule = moduleKey;
                console.log('🎉 Module completed!');
            }

            // Add data parameters if provided
            if (Object.keys(dataParameters).length > 0) {
                updates[`modules.${moduleKey}.moduleDataParameters`] = {
                    ...(snap.data()?.modules?.[moduleKey]?.moduleDataParameters || {}),
                    ...dataParameters
                };
            }

            await updateDoc(progressRef, updates);
            console.log(`✅ Module progress saved`);
            console.groupEnd();
        }, `update_module_progress_${moduleKey}`);
    },

    /**
     * Add question attempt to module
     */
    async addQuestionAttempt(userId, level, chapter, moduleKey, questionNum, questionData, dataParameters = {}) {
        return silentTrack(async () => {
            const progressRef = getLearningProgressRef(userId, level, chapter);
            const updates = {
                [`modules.${moduleKey}.questions.${questionNum}`]: {
                    ...questionData,
                    dateAttempted: serverTimestamp()
                },
                [`modules.${moduleKey}.lastQuestionAnswered`]: questionNum,
                lastUpdated: serverTimestamp()
            };

            // Add data parameters if provided
            if (Object.keys(dataParameters).length > 0) {
                updates[`modules.${moduleKey}.questions.${questionNum}.questionDataParameters`] = {
                    ...dataParameters
                };
            }

            await updateDoc(progressRef, updates);
            console.log(`✓ Question ${questionNum} saved for ${moduleKey}`);
        }, `add_question_attempt_${moduleKey}`);
    },

    /**
     * Get LearningProgress data
     */
    async getLearningProgress(userId, level, chapter) {
        return silentTrack(async () => {
            const progressRef = getLearningProgressRef(userId, level, chapter);
            const snap = await getDoc(progressRef);
            return snap.exists() ? snap.data() : null;
        }, 'get_learning_progress');
    }
};
